import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import { SmartCapitalizePipe } from '../../utils/pipes/smart-capitalize.pipe';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { toSlug } from '../../utils/slugs/slug.utils';
import { ModeloAniosAplica, ModeloVehiculo } from '../../types/vehicle.types';
import { getDataFromResolver } from '../../services/resolvers';
import { Resolvers } from '../../types/Resolvers';

// Interface para agrupar por marca
interface ModelosPorMarca {
  marca: string;
  modelos: ModeloVehiculo[];
}

@Component({
  selector: 'allas-baterias-marca-modelo',
  imports: [
    MainLayout,
    FormsModule,
    CommonModule,
    RouterModule,
    SmartCapitalizePipe,
    MarginWrapper,
  ],
  template: `
    <allas-main-layout>
      <allas-margin-wrapper styleClass="fs-condensed">
        <!-- Header Section con Título y Búsqueda -->
        <nav
          class="py-8 lg:py-12"
          role="navigation"
          aria-label="Navegación principal"
        >
          <div class="container mx-auto px-4 lg:px-8">
            <div class="max-w-4xl mx-auto text-center">
              <!-- Titulo Principal oculto pero indexable -->
              <h1 class="absolute left-[-9999px]">
                Baterías para automóviles en Honduras, mejores precios y envío a
                domicilio.
              </h1>
              <h2
                class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 font-inter"
              >
                Batería para los carros más populares en Honduras
              </h2>
              <p class="text-lg text-gray-600 mb-8 font-inter">
                Allas Repuestos tiene una amplia selección de baterías,
                acumuladores, para carros, camionetas, camiones, etc. para cada
                marca y modelo de vehículo en esta lista. Podés buscar por
                marca, modelo o año de vehículo.
              </p>

              <!-- Search Bar -->
              <div class="max-w-md mx-auto">
                <label for="battery-search" class="sr-only">
                  Buscar batería por marca, modelo o año
                </label>
                <div class="relative">
                  <input
                    id="battery-search"
                    type="text"
                    [ngModel]="searchTerm()"
                    (ngModelChange)="searchTerm.set($event)"
                    placeholder="Buscar batería por marca, modelo o año..."
                    class="w-full px-4 py-3 pl-12 pr-4 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all duration-200 placeholder:text-gray-500"
                    aria-describedby="search-description"
                  />
                  <svg
                    class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    ></path>
                  </svg>
                </div>
                <div id="search-description" class="sr-only">
                  Busca baterías específicas por marca, modelo o año de vehículo
                </div>
              </div>
            </div>
          </div>
        </nav>

        <div class="px-4 py-8 fs-condensed">
          <!-- No Results Message -->
          @if (searchTerm() && filteredBrandsGrouped().length === 0) {
          <div class="text-center py-12">
            <div class="text-gray-400 mb-4">
              <svg
                class="mx-auto w-12 h-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2 font-inter">
              No se encontraron baterías
            </h3>
            <p class="text-gray-600 mb-6 font-inter">
              No encontramos baterías que coincidan con "{{ searchTerm() }}"
            </p>
            <div class="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
              <h4 class="font-semibold text-gray-900 mb-2 font-inter">
                ¿No encuentras tu batería?
              </h4>
              <p class="text-sm text-gray-600 mb-4 font-inter">
                Contáctanos y te ayudamos a encontrar la batería perfecta para
                tu vehículo
              </p>
              <div class="space-y-2 text-sm">
                <div class="flex items-center gap-2 text-gray-600">
                  <span class="font-medium">WhatsApp:</span>
                  <a
                    href="https://wa.me/50495089953"
                    class="text-red-600 hover:text-red-700 transition-colors"
                  >
                    +504 9508-9953
                  </a>
                </div>
                <div class="flex items-center gap-2 text-gray-600">
                  <span class="font-medium">Email:</span>
                  <a
                    href="mailto:servicioalcliente@allasrepuestos.com"
                    class="text-red-600 hover:text-red-700 transition-colors"
                  >
                    servicioalcliente&#64;allasrepuestos.com
                  </a>
                </div>
              </div>
            </div>
          </div>
          } @else {
          <!-- Lista agrupada por marca -->
          <div class="columns-1 md:columns-3 lg:columns-4 gap-8 space-y-8">
            @for (brandGroup of filteredBrandsGrouped(); track brandGroup.marca)
            {
            <div class="break-inside-avoid space-y-4">
              <h3 class="text-lg font-bold mb-4 pb-2 border-b border-gray-300">
                {{ brandGroup.marca | smartCapitalize }}
              </h3>
              <ul class="space-y-2">
                @for ( model of brandGroup.modelos; track model.marca +
                model.modelo; let isLast = $last ) { @for (year of
                getAvailableYearsForModel(model.marca, model.modelo); track
                year) {
                <li>
                  <!-- Antes apunta a /baterias y se unia con guiones la marca,modelo y año -->
                  <a
                    [routerLink]="[
                      '/repuestos',
                      'bateria-para-carro-auto-o-camioneta',
                      toSlug(model.marca),
                      toSlug(model.modelo),
                      year.toString()
                    ]"
                    class="text-gray-700 hover:text-allasRed text-sm block cursor-pointer"
                  >
                    Batería para
                    {{ model.marca | smartCapitalize }}
                    {{ model.modelo | smartCapitalize }}
                    {{ year }}
                  </a>
                </li>
                } @if (!isLast) {
                <li class="my-3">
                  <hr class="border-gray-200" />
                </li>
                } }
              </ul>
            </div>
            }
          </div>
          }
        </div>
      </allas-margin-wrapper>
    </allas-main-layout>
  `,
})
export class BateriasMarcaModelo implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  searchTerm = signal('');

  // Pre-calcular todos los datos para evitar cálculos en el template
  private readonly aniosDisponiblesMap = new Map<string, number[]>();
  private readonly modelosConAnios: Array<{
    marca: string;
    modelo: string;
    anios: number[];
  }> = [];

  // Obtener datos del resolver
  vehicleModels: ModeloVehiculo[] = [];

  // Exponer toSlug al template
  toSlug = toSlug;

  /**
   * Obtiene los años disponibles para un modelo específico (usando datos pre-calculados)
   */
  getAvailableYearsForModel(marca: string, modelo: string): number[] {
    const key = `${marca.toUpperCase()}_${modelo.toUpperCase()}`;
    return this.aniosDisponiblesMap.get(key) || [];
  }

  constructor() {}

  ngOnInit() {
    this.setMetaTags();
    this.initializeVehicleModels();
  }

  private initializeVehicleModels() {
    // Inicializar datos desde el resolver
    const preloadedData = getDataFromResolver<ModeloAniosAplica[]>(
      Resolvers.TOP_VEHICLES_YEARS_MODELS,
      this.route
    );

    const models: ModeloVehiculo[] = [];
    const currentYear = new Date().getFullYear();

    if (preloadedData && preloadedData.length > 0) {
      preloadedData.forEach((item: ModeloAniosAplica) => {
        const marca = item.marca.toUpperCase();
        const modelo = item.modelo.toUpperCase();
        const key = `${marca}_${modelo}`;

        // Calcular años disponibles una sola vez
        let aniosDisponibles: number[] = [];
        if (item.anios_aplica && item.anios_aplica.length > 0) {
          const minYear = Math.min(...item.anios_aplica);
          const maxYear = Math.max(...item.anios_aplica);
          const startYear = minYear < 1980 ? 1980 : minYear;
          const endYear = Math.min(maxYear, currentYear);

          const filteredYears = item.anios_aplica.filter(
            (year) => year >= startYear && year <= endYear
          );

          aniosDisponibles =
            filteredYears.length > 0
              ? filteredYears.sort((a, b) => a - b)
              : Array.from(
                  { length: endYear - startYear + 1 },
                  (_, index) => startYear + index
                );
        } else {
          // Años por defecto si no hay datos
          aniosDisponibles = Array.from(
            { length: currentYear - 2000 + 1 },
            (_, index) => 2000 + index
          );
        }

        // Guardar en el mapa
        this.aniosDisponiblesMap.set(key, aniosDisponibles);

        // Guardar modelo con años pre-calculados
        this.modelosConAnios.push({
          marca,
          modelo,
          anios: aniosDisponibles,
        });

        // Agregar al array de modelos
        models.push({
          marca: item.marca,
          modelo: item.modelo,
        } as ModeloVehiculo);
      });
    }

    this.vehicleModels = models;
  }

  private setMetaTags() {
    const route = this.router.url;
    const pageTitle =
      'Baterías por Marca y Modelo de Vehículo | Allas Repuestos';
    const description =
      'Encuentra la batería perfecta para tu vehículo por marca y modelo. Amplia selección con envío a domicilio en Honduras.';
    const keywords =
      'baterías, repuestos, vehículos, marcas, modelos, Honda, Toyota, Nissan, Ford, Chevrolet, envío a domicilio, Honduras, Allas Repuestos, instalación, garantía';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    // --- Título de la página ---
    this.title.setTitle(pageTitle);

    // --- Meta Descripción ---
    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    // --- Palabras Clave ---
    this.meta.updateTag({
      name: 'keywords',
      content: keywords,
    });

    // --- Open Graph Tags ---
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });
    this.meta.updateTag({ property: 'og:url', content: route });

    // --- Twitter Tags ---
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // --- Icono del sitio (favicon / logo principal) ---
    this.meta.updateTag({
      name: 'icon',
      content: imageUrl,
    });
  }

  // Agrupar modelos por marca
  private modelosAgrupadosPorMarca = computed(() => {
    // Usar Map para preservar el orden de inserción
    const modelosPorMarca = new Map<string, ModeloVehiculo[]>();
    const ordenMarcas: string[] = [];

    this.vehicleModels.forEach((item) => {
      const marca = item.marca.toUpperCase();

      if (!modelosPorMarca.has(marca)) {
        modelosPorMarca.set(marca, []);
        ordenMarcas.push(marca);
      }

      modelosPorMarca.get(marca)!.push(item);
    });

    // Convertir a array manteniendo el orden original
    return ordenMarcas.map((marca) => ({
      marca,
      modelos: modelosPorMarca.get(marca)!,
    }));
  });

  // Filtrar marcas y modelos basado en el término de búsqueda
  filteredBrandsGrouped = computed(() => {
    const grouped = this.modelosAgrupadosPorMarca();

    if (!this.searchTerm()) {
      return grouped;
    }

    const searchLower = this.searchTerm().toLowerCase().trim();

    return grouped
      .map((brandGroup) => {
        // Filtrar modelos que coincidan con la búsqueda
        const modelosFiltrados = brandGroup.modelos.filter((model) => {
          const marcaLower = model.marca.toLowerCase();
          const modeloLower = model.modelo.toLowerCase();

          // Buscar coincidencias en marca o modelo
          if (
            marcaLower.includes(searchLower) ||
            modeloLower.includes(searchLower)
          ) {
            return true;
          }

          // Buscar en combinaciones completas "Batería para Marca Modelo Año"
          // Usar datos pre-calculados en lugar de llamar a la función
          const key = `${model.marca.toUpperCase()}_${model.modelo.toUpperCase()}`;
          const availableYears = this.aniosDisponiblesMap.get(key) || [];

          return availableYears.some((year) => {
            const fullText = `batería para ${marcaLower} ${modeloLower} ${year}`;
            return fullText.includes(searchLower);
          });
        });

        return {
          marca: brandGroup.marca,
          modelos: modelosFiltrados,
        };
      })
      .filter((brandGroup) => brandGroup.modelos.length > 0);
  });

  getBrandsGrouped(): ModelosPorMarca[] {
    return this.modelosAgrupadosPorMarca();
  }
}
